import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';

import { FieldCoordinatePicker } from '@/components/Fields/FieldCoordinatePicker';
import { parsePointCoordinate } from '@/utils/fieldCoordinate';

interface FieldLocationCardProps {
  coordinate: string | null;
  fieldName?: string;
}

const CARD_HEIGHT = 140;
const THUMBNAIL_MAP_HEIGHT = 84;

export const FieldLocationCard: React.FC<FieldLocationCardProps> = ({ coordinate, fieldName }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const position = useMemo(() => parsePointCoordinate(coordinate), [coordinate]);

  if (!position) {
    return (
      <Card sx={{ height: CARD_HEIGHT }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Location
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Not specified
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card
        sx={{
          height: CARD_HEIGHT,
          cursor: 'pointer',
          '&:hover': { boxShadow: 3 },
        }}
        onClick={() => setDialogOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setDialogOpen(true);
          }
        }}
      >
        <CardContent
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            p: 2,
            '&:last-child': { pb: 2 },
          }}
        >
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Location
          </Typography>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <FieldCoordinatePicker
              enabled={!dialogOpen}
              position={position}
              onChange={() => undefined}
              readOnly
              mapHeight={THUMBNAIL_MAP_HEIGHT}
              showHint={false}
              showCoordinates={false}
              showControls={false}
              markerKind="field"
              pinLabel={fieldName}
            />
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="body"
      >
        <DialogTitle>Location</DialogTitle>
        <DialogContent>
          <FieldCoordinatePicker
            enabled={dialogOpen}
            position={position}
            onChange={() => undefined}
            readOnly
            showHint
            markerKind="field"
            pinLabel={fieldName}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
